import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)

// Detect the device's IANA timezone once (e.g. "America/New_York")
export const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone

dayjs.tz.setDefault(USER_TIMEZONE)

export default dayjs

// ---------------------------------------------------------------------------
// Outgoing helpers — use when sending values to the API
// ---------------------------------------------------------------------------

/**
 * Converts any local date/string to a UTC ISO-8601 string for the API.
 * e.g. "2024-06-01T14:00:00" (Paris) → "2024-06-01T12:00:00.000Z"
 */
export const toUtcIso = (date: string | Date | dayjs.Dayjs): string =>
  dayjs(date).utc().toISOString()

/**
 * Converts a local date-only value to a UTC midnight ISO string.
 * e.g. "2024-06-01" → "2024-05-31T22:00:00.000Z" (UTC+2)
 */
export const toUtcDateIso = (date: string | Date | dayjs.Dayjs): string =>
  dayjs.tz(dayjs(date).format('YYYY-MM-DD'), USER_TIMEZONE).utc().toISOString()

// ---------------------------------------------------------------------------
// Incoming helpers — use when displaying values received from the API
// ---------------------------------------------------------------------------

/**
 * Parses a UTC ISO string from the API and returns a dayjs in the user's local timezone.
 * e.g. "2024-06-01T12:00:00.000Z" → dayjs at 14:00 (Paris/UTC+2)
 */
export const fromApiDate = (isoStr: string): dayjs.Dayjs =>
  dayjs.utc(isoStr).tz(USER_TIMEZONE)

/**
 * Parses a UTC ISO string and formats it using the given pattern in the user's timezone.
 * e.g. fromApiDateFormat("2024-06-01T12:00:00.000Z", "DD/MM/YYYY HH:mm") → "01/06/2024 14:00"
 */
export const fromApiDateFormat = (isoStr: string, format: string): string =>
  dayjs.utc(isoStr).tz(USER_TIMEZONE).format(format)

// ---------------------------------------------------------------------------
// Time-only helpers — use for "HH:mm" fields (no date context)
// ---------------------------------------------------------------------------

/**
 * Converts a local "HH:mm" string to a UTC "HH:mm" string for storing via the API.
 * e.g. "14:00" (UTC+2) → "12:00"
 */
export const toUtcTime = (localHhmm: string): string => {
  const [h, m] = localHhmm.split(':').map(Number)
  return dayjs().hour(h).minute(m).second(0).utc().format('HH:mm')
}

/**
 * Converts a UTC "HH:mm" string received from the API to the user's local "HH:mm".
 * e.g. "12:00" (UTC) → "14:00" (UTC+2)
 */
export const fromApiTime = (utcHhmm: string): string => {
  const [h, m] = utcHhmm.split(':').map(Number)
  return dayjs.utc().hour(h).minute(m).second(0).tz(USER_TIMEZONE).format('HH:mm')
}
